import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRothko } from "web/react/hooks/use-rothko/use-rothko";
import {
    setFiltersActiveFilters,
    setFiltersData,
    setFiltersFeedSortBy,
    setFiltersFeedUrl,
    setFiltersIsFetching,
    setFiltersIsStale,
    setFiltersOnApplySortBy,
    setFiltersOnApplyUrl,
} from "web/redux/ducks/filters";
import { ReduxStoreState } from "web/redux/store";
import history from "web/script/utils/history";
import {
    ActiveFilterSerializer,
    FeedFiltersSerializer,
    FilterOptionSerializer,
} from "web/types/serializers";

export interface UseFiltersSyncResult {
    onChange(url: string, shouldApply?: boolean): void;
    onOptionChange(filterOption: FilterOptionSerializer): void;
    onApply(): void;
    onClose(): void;
    onReset(): void;
    refetchFilters(): void;
    isFetching: boolean;
}

const apply = (url: string): void => {
    history.pushState(new URL(url, window.location.href));
};

const generateQueryUrl = (queryUrlList: string[]): string => {
    const { pathname, search } = new URL(`?${queryUrlList.join("&")}`, window.location.href);
    return pathname + search;
};

export function useFilterSync(): UseFiltersSyncResult {
    const dispatch = useDispatch();
    const { ui, data } = useSelector((state: ReduxStoreState) => state.filters);
    const { isFetching: uiIsFetching, sortBy, url: uiUrl, isStale, activeFilters, baseUrl } = ui;

    const { isFetching: rothkoIsFetching, run } = useRothko<FeedFiltersSerializer>(
        "modules/filters",
        { feed_url: null },
        {
            enabled: false,
            rothko: {
                cache: true,
            },
            onSuccess: (data) => dispatch(setFiltersData(data)),
        }
    );

    const refetchFilters = useCallback(async () => {
        if (isStale && uiUrl) {
            await run(`?feed_url=${encodeURIComponent(uiUrl)}`);
            dispatch(setFiltersIsStale(false));
        }
    }, [run, uiUrl, isStale, dispatch]);

    const onChange = useCallback(
        (url: string, shouldApply = false) => {
            dispatch(setFiltersFeedUrl(url));
            run(`?feed_url=${encodeURIComponent(url)}`);
            shouldApply && apply(url);
        },
        [dispatch, run]
    );

    const onOptionChange = useCallback(
        (filterOption: FilterOptionSerializer) => {
            const isActiveFilter = activeFilters.find((option) => filterOption.id === option.id);
            const filters = isActiveFilter
                ? activeFilters.filter((queryParam) => queryParam.id !== filterOption.id)
                : [...activeFilters, filterOption];
            const queryParamsList = filters
                .filter((option) => option.query_param)
                .map((option) => option.query_param) as string[];
            dispatch(setFiltersActiveFilters(filters));
            dispatch(setFiltersFeedUrl(generateQueryUrl(queryParamsList)));
            dispatch(setFiltersIsStale(true));
        },
        [activeFilters, dispatch]
    );

    const onApply = useCallback(() => {
        if (uiUrl) {
            refetchFilters();
            const newUrl = new URL(uiUrl, window.location.href);
            if (sortBy === "recommended") {
                newUrl.searchParams.delete("view");
            } else if (sortBy) {
                newUrl.searchParams.set("view", sortBy);
            }
            const { pathname, search } = newUrl;
            apply(pathname + search);
            dispatch(setFiltersOnApplyUrl(pathname + search));
            dispatch(setFiltersOnApplySortBy(sortBy));
            dispatch(setFiltersFeedUrl(pathname + search));
        }
    }, [uiUrl, sortBy, refetchFilters]);

    const onClose = useCallback(() => {
        refetchFilters();
        const currentSortBy =
            new URLSearchParams(window.location.search).get("view") || "recommended";
        if ((uiUrl && uiUrl !== window.location.pathname) || currentSortBy !== sortBy) {
            dispatch(setFiltersFeedSortBy(currentSortBy));
            const currentUrl = window.location.pathname + window.location.search;
            onChange(currentUrl, false);
        }
    }, [uiUrl, sortBy, refetchFilters]);

    const onReset = useCallback(() => {
        dispatch(setFiltersFeedSortBy("recommended"));
        if (baseUrl) {
            onChange(baseUrl, true);
        }
    }, [baseUrl]);

    useEffect(() => {
        dispatch(setFiltersIsFetching(rothkoIsFetching));
    }, [dispatch, rothkoIsFetching]);

    useEffect(() => {
        const activeFilters =
            (data && data.active_filters && data.active_filters) ||
            ([] as ActiveFilterSerializer[]);
        dispatch(setFiltersActiveFilters(activeFilters));
    }, [dispatch, data]);

    return {
        onChange,
        onApply,
        onClose,
        onReset,
        refetchFilters,
        onOptionChange,
        isFetching: uiIsFetching,
    };
}
